import { useState } from 'react';

import { Loader2, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api/axios';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type { CollectFilter, CollectNodeData } from '@repo/shared-types';

const ENTITY_OPTIONS: { value: CollectNodeData['entity']; label: string }[] = [
  { value: 'bookings', label: 'Bookings' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'calls', label: 'Calls' },
  { value: 'payment_links', label: 'Payment Links' },
  { value: 'stock_listings', label: 'Stock Listings' },
];

const OPERATOR_OPTIONS: { value: CollectFilter['operator']; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In (comma list)' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
  { value: 'within', label: 'Within (date)' },
];

const VALUELESS_OPERATORS = new Set<CollectFilter['operator']>(['is_empty', 'is_not_empty']);

interface CollectConfigProps {
  data: CollectNodeData;
  onChange: (data: CollectNodeData) => void;
}

interface CollectPreviewResult {
  count: number;
  items: unknown[];
  truncated: boolean;
  reference_date: string;
}

export const CollectConfig = ({ data, onChange }: CollectConfigProps) => {
  const update = (fields: Partial<CollectNodeData>) => {
    onChange({ ...data, ...fields });
  };

  const [previewDate, setPreviewDate] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<CollectPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    setPreviewResult(null);
    try {
      const { data: result } = await apiClient.post('/workflows/collect-preview', {
        data,
        ...(previewDate ? { date: previewDate } : {}),
      });
      setPreviewResult(result);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Preview failed');
      setPreviewError(message);
    } finally {
      setPreviewing(false);
    }
  };

  const filters = data.filters ?? [];

  const updateFilter = (index: number, patch: Partial<CollectFilter>) => {
    update({ filters: filters.map((f, i) => (i === index ? { ...f, ...patch } : f)) });
  };

  const addFilter = () => {
    update({ filters: [...filters, { field: '', operator: 'equals', value: '' }] });
  };

  const removeFilter = (index: number) => {
    update({ filters: filters.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Label</label>
        <Input
          value={data.label || ''}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Collect"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Entity</label>
        <Select
          value={data.entity || 'bookings'}
          onValueChange={(v) => update({ entity: v as CollectNodeData['entity'] })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ENTITY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-gray-400">Filters</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={addFilter}
          >
            + Add
          </Button>
        </div>
        <div className="space-y-2">
          {filters.map((filter, index) => (
            <div key={index} className="flex gap-1">
              <Input
                value={filter.field}
                onChange={(e) => updateFilter(index, { field: e.target.value })}
                placeholder="Field"
                className="flex-1"
              />
              <Select
                value={filter.operator}
                onValueChange={(v) =>
                  updateFilter(index, { operator: v as CollectFilter['operator'] })
                }
              >
                <SelectTrigger className="w-[120px] shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OPERATOR_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!VALUELESS_OPERATORS.has(filter.operator) && (
                <VariableAutocomplete
                  value={filter.value ?? ''}
                  onChange={(v) => updateFilter(index, { value: v })}
                  placeholder="Value"
                  className="flex-1"
                />
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-red-500 hover:text-red-600"
                onClick={() => removeFilter(index)}
              >
                ×
              </Button>
            </div>
          ))}
        </div>
        <p className="mt-1 text-[10px] text-gray-500">
          {
            '`within` on date fields accepts today, tomorrow, yesterday, next_7_days, past_7_days, next_24h, past_24h or an explicit YYYY-MM-DD. Values support {{template}} variables.'
          }
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Dedupe By (optional)</label>
        <Input
          value={data.dedupeBy || ''}
          onChange={(e) => update({ dedupeBy: e.target.value })}
          placeholder="e.g. contact.phone"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Dotted path — keeps only the first row per value (after ordering).
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Order By (optional)</label>
        <div className="flex gap-1">
          <Input
            value={data.orderBy || ''}
            onChange={(e) => update({ orderBy: e.target.value })}
            placeholder="Default per entity"
            className="flex-1"
          />
          <Select
            value={data.orderDirection || 'asc'}
            onValueChange={(v) => update({ orderDirection: v as 'asc' | 'desc' })}
          >
            <SelectTrigger className="w-[100px] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Ascending</SelectItem>
              <SelectItem value="desc">Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Limit</label>
        <Input
          type="number"
          min={1}
          max={1000}
          value={data.limit ?? 200}
          onChange={(e) => update({ limit: Number(e.target.value) })}
          placeholder="200"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Output Key</label>
        <Input
          value={data.outputKey || 'collected'}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="collected"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          The collected rows land in context as an array under this key, e.g.{' '}
          {'{{collected.length}}'}.
        </p>
      </div>

      <div className="rounded-md border p-3">
        <label className="mb-1 block text-xs font-medium text-gray-400">Preview</label>
        <div className="flex gap-1">
          <Input
            type="date"
            value={previewDate}
            onChange={(e) => setPreviewDate(e.target.value)}
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0 px-3 text-xs"
            onClick={handlePreview}
            disabled={previewing}
          >
            {previewing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Play className="h-3 w-3" />
            )}
            <span className="ml-1">Preview</span>
          </Button>
        </div>
        <p className="mt-1 text-[10px] text-gray-500">
          Runs this query now. The optional date anchors relative windows — “within tomorrow”
          previewed as if today were that date. Nothing is created.
        </p>

        {previewError && <p className="mt-2 text-[11px] text-red-500">{previewError}</p>}

        {previewResult && (
          <div className="mt-2">
            <p className="text-[11px] font-medium text-gray-300">
              {previewResult.count} row(s)
              {previewResult.truncated ? ` — showing first ${previewResult.items.length}` : ''} · as
              of {previewResult.reference_date}
            </p>
            {previewResult.items.length > 0 && (
              <pre className="bg-muted/30 mt-1 max-h-64 overflow-auto rounded-md border p-2 font-mono text-[10px] leading-relaxed text-gray-400">
                {JSON.stringify(previewResult.items, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
